import { useEffect, useMemo, useState } from "react";
import { Box, Divider, InputAdornment, Paper, Skeleton, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import {
  ChoiceToggle,
  FieldWrapper,
  OptionCardGroup,
  PageHeader,
  SectionHeader,
  StepActions,
  TextFieldSelectIcon,
} from "@components/ui_v2";
import { FormActivoVehiculo } from "@components/ui_v2/activo/FormActivoVehiculo";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setLeasingTypeId } from "@store/slices/registerSlice";
import {
  useLazyGetLoanApplicationByIdQuery,
  usePostValidateNextStageMutation,
  usePutApplicationCompletedMutation,
} from "@api/applications";
import { useLazyGetLeasingTypeConfigQuery } from "@api/catalogs";
import { useLazyGetLeasingListQuery, usePatchLeasingMutation, usePostLeasingMutation } from "@api/leasing";
import { LeasingType } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { LeasingStructure } from "@interfaces/LeasingTypes";
import { AssetForm } from "@interfaces/seleccionActivo";
import useStatusConfig from "@hooks/useStatusConfig";
import useLibroAzul from "@hooks/useLibroAzul";
import { STATUS_CATALOG } from "@helpers/constantes";
import { formatCurrencyInput } from "@helpers/funciones";
import {
  AssetDetails,
  AUTO_TYPE,
  buildAssetLeasing,
  buildAssetSchema,
  CONDITIONS,
  EMPTY_ASSET_FORM,
  hydrateAsset,
  mockFamilyOf,
  toLeasingTypes,
} from "@helpers/seleccionActivoHelpers";

const SCREEN_STATUS = STATUS_CATALOG.seleccion_activo.code;

const GRID_SX = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" } as const;
const FULL_SX = { gridColumn: "1 / -1" } as const;

/**
 * Pymio · flujo activo (`SeleccionActivoPymio` del diseño): familia del activo en tarjetas, el tipo en un select con
 * ícono y la descripción del activo. Las familias son los leasing types del tax system de la solicitud
 * (`leasing_type_config`) que están en el prototipo, con el del cotizador (`leasing_type_id`) preseleccionado. Los
 * vehículos se capturan con `FormActivoVehiculo` (autos y SUVs con el Libro Azul); los demás activos, con una
 * descripción libre.
 *
 * Al continuar:
 * 1. Si eligió otra familia, actualiza el `leasing_type_id` de la solicitud.
 * 2. Guarda el activo en `leasing` (POST la primera vez, PATCH si ya existe). Al regresar se precarga lo guardado.
 * 3. Avanza el status (CDSIA) y navega a la URL que manda `status_config`.
 *
 * TODO: el ícono y los tipos de cada familia salen del MOCK (`seleccionActivoMock`, cruzado por código).
 */
export const SeleccionActivo = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(SCREEN_STATUS);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();
  const [putApplication] = usePutApplicationCompletedMutation();
  const [getLeasingTypeConfig] = useLazyGetLeasingTypeConfigQuery();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [postLeasing] = usePostLeasingMutation();
  const [patchLeasing] = usePatchLeasingMutation();
  const libroAzul = useLibroAzul();

  const [leasingTypes, setLeasingTypes] = useState<LeasingType[]>([]);
  /** `leasing_type_id` guardado en la solicitud: solo si el usuario elige otro se actualiza al continuar. */
  const [savedLeasingTypeId, setSavedLeasingTypeId] = useState<string | null>(null);
  /** Id del leasing ya guardado: si existe se hace PATCH; si no, POST. */
  const [leasingId, setLeasingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const mockOf = (leasingTypeId: string) => mockFamilyOf(leasingTypes.find((item) => item.id === leasingTypeId)?.code);
  const isVehicleFamily = (leasingTypeId: string) => mockOf(leasingTypeId)?.id === "vehicular";

  const validationSchema = useMemo(
    () => buildAssetSchema({ isVehicle: isVehicleFamily, hasTypes: (id) => Boolean(mockOf(id)?.tipos.length) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leasingTypes],
  );

  const formik = useFormik<AssetForm>({
    initialValues: EMPTY_ASSET_FORM,
    validateOnMount: true,
    validationSchema,
    onSubmit: async (form, { setSubmitting }) => {
      try {
        setSubmitting(true);
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }
        if (form.family !== savedLeasingTypeId) {
          await putApplication({ id_sol: application.id, body: { leasing_type_id: form.family } }).unwrap();
          setSavedLeasingTypeId(form.family);
          dispatch(setLeasingTypeId({ leasing_type_id: form.family }));
        }
        const leasing = buildAssetLeasing({
          applicationId: application.id,
          form,
          type: mockOf(form.family)?.tipos.find((item) => item.id === form.type),
          isVehicle: isVehicleFamily(form.family),
          libroAzulNames: libroAzul.names(form),
        });
        if (leasingId) {
          await patchLeasing({ id_solicitud: application.id, vehicleId: leasingId, data: leasing }).unwrap();
        } else {
          await postLeasing({ id_solicitud: application.id, data: { leasings_list: [leasing] } }).unwrap();
          // El POST no regresa el id: se consulta para que un reintento (si falla el avance del status) haga PATCH
          // en lugar de crear otro registro.
          const [created] = await getLeasingList(application.id).unwrap();
          setLeasingId(created?.id ?? null);
        }
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();
        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "SeleccionActivo-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // De la solicitud (y no del store) para que funcione también al recargar.
        const [loanApplication, leasings] = await Promise.all([
          getLoanApplication(application.id).unwrap(),
          getLeasingList(application.id).unwrap() as Promise<LeasingStructure<AssetDetails>[]>,
        ]);
        const items = toLeasingTypes(
          await getLeasingTypeConfig({ tax_system_id: String(loanApplication.tax_systems_id) }).unwrap(),
        );
        setLeasingTypes(items);
        setSavedLeasingTypeId(loanApplication.leasing_type_id ?? null);
        const initial = items.find((item) => item.id === loanApplication.leasing_type_id) ?? items[0];
        if (!initial) return;

        const types = mockFamilyOf(initial.code)?.tipos ?? [];
        const base = { ...EMPTY_ASSET_FORM, family: initial.id, type: types[0]?.id ?? "" };
        const saved = leasings[0];
        if (!saved?.id) {
          formik.setValues(base);
          return;
        }
        setLeasingId(saved.id);
        const { values, autoDetails } = hydrateAsset(base, saved, types);
        formik.setValues(autoDetails ? { ...values, ...(await libroAzul.hydrate(autoDetails)) } : values);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "SeleccionActivo-initial_data" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { values } = formik;
  const family = mockOf(values.family);
  const isVehicle = family?.id === "vehicular";

  /** Cambiar de familia o de tipo limpia lo que se describió del activo anterior. */
  const resetAsset = (patch: Partial<AssetForm>) => {
    libroAzul.reset();
    formik.setValues({ ...values, ...patch, year: "", brand: "", model: "", version: "", description: "" });
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Activo"
        title="¿Qué activo vas a financiar?"
        subtitle="Elige el tipo de activo y descríbelo. Las financieras lo usan para armar su oferta de arrendamiento o crédito."
      />

      <Paper variant="lendia_paper">
        <SectionHeader label="Tipo de activo" first />
        {isLoading ? (
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: "12px" }} />
        ) : (
          <OptionCardGroup
            layout="tiles"
            value={values.family}
            onChange={(value) => resetAsset({ family: value, type: mockOf(value)?.tipos[0]?.id ?? "" })}
            options={leasingTypes.map((item) => ({
              value: item.id,
              label: item.name,
              sub: item.description,
              icon: mockFamilyOf(item.code)?.icon,
              testId: `activo-familia-${item.code}`,
            }))}
          />
        )}

        <Divider sx={{ mt: "20px", mb: "18px", opacity: 0.6 }} />

        <Box sx={GRID_SX}>
          {family && family.tipos.length > 0 && (
            <Box sx={FULL_SX}>
              <FieldWrapper label={family.tipoLabel} full error={formik.touched.type ? formik.errors.type : undefined}>
                <TextFieldSelectIcon
                  value={values.type}
                  options={family.tipos.map((type) => ({
                    value: type.id,
                    label: type.label,
                    sub: type.sub,
                    icon: type.icon,
                  }))}
                  onChange={(value) => resetAsset({ type: value })}
                  onBlur={() => formik.setFieldTouched("type", true)}
                  testId="activo-tipo-select"
                />
              </FieldWrapper>
            </Box>
          )}

          {isVehicle ? (
            <FormActivoVehiculo
              auto={values.type === AUTO_TYPE}
              values={values}
              libroAzul={libroAzul}
              onChange={(patch) => formik.setValues({ ...values, ...patch })}
            />
          ) : (
            <Box sx={FULL_SX}>
              <FieldWrapper label="Describe el activo" hint="Marca, modelo y lo que nos ayude a identificarlo" full>
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  fullWidth
                  value={values.description}
                  onChange={(event) => formik.setFieldValue("description", event.target.value)}
                  placeholder="Ej. Excavadora CAT 320, 2022, 1,200 horas"
                  inputProps={{ "data-testid": "activo-descripcion-input" }}
                />
              </FieldWrapper>
            </Box>
          )}

          <FieldWrapper
            label="Valor del activo"
            hint="Con IVA, de la cotización o factura"
            error={formik.touched.amount ? formik.errors.amount : undefined}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={values.amount}
              onBlur={() => formik.setFieldTouched("amount", true)}
              onChange={(event) => formik.setFieldValue("amount", formatCurrencyInput(event.target.value))}
              placeholder="0"
              inputProps={{ inputMode: "numeric", maxLength: 20, "data-testid": "activo-valor-input" }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </FieldWrapper>

          <FieldWrapper label="Condición">
            <ChoiceToggle
              value={values.condition}
              onChange={(value) => formik.setFieldValue("condition", String(value))}
              options={CONDITIONS}
              testId="activo-condicion"
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        onBack={() => previousStatus && navigate(previousStatus.url)}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || formik.isSubmitting}
        backTestId="activo-back-button"
        nextTestId="activo-continue-button"
      />
    </Box>
  );
};
